import { Download } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";

import Search from "../../../components/Search";
import {
  create,
  deleteCountry,
  exportAll,
  update,
  useList,
  isInUse,
} from "../../../services/countries";
import { getLocal } from "../../../storage/auth";
import { Country } from "../../../types/country";
import CountryForm from "./components/CountryForm";
import DataTable from "./components/DataTable";
import { hasClaim } from "../../../storage/userClaims";
import { useSnackbar } from "notistack";

interface IParams {
  page: number;
  keyword: string;
  sort: string;
  limit: number;
  token: string;
}
export default function Countries() {
  const { enqueueSnackbar } = useSnackbar();
  const [country, setCountry] = useState<Country>({ ATIVO: true } as Country);
  const [controlEdit, setControlEdit] = useState({
    loading: false,
    isInUse: false,
  });
  const [countryId, setCountryId] = useState<string | number>();
  const [isSaving, setIsSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [userValidation, setUserValidation] = useState<string>();
  const [readOnly, setReadOnly] = useState<boolean>(false);


  const getTokenSession = () => {
    const getUser = (getLocal("userInfo")) as any;
    setUserValidation(JSON.parse(getUser));
  };
  useEffect(() => {
    getTokenSession();
  }, []);

  const [params, setParams] = useState<IParams>({
    page: 1,
    limit: 10,
  } as IParams);

  const [errors, setErrors] = useState<string[]>();
  const { data, meta, isLoading } = useList(params);

  const handleRequest = (requestParams: any) => {
    setParams((prevState) => ({ ...prevState, ...requestParams }));
  };

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const handleEditCountry = async (id: string | number) => {
    setErrors([])
    setReadOnly(false)
    const editCountry = data.findLast((item: any) => item.ID_PAIS === id);
    if (editCountry) {
      const { ID_PAIS, SIGLA_PAIS, NOME_PAIS, ATIVO } = editCountry;
      setCountry({ ID_PAIS, SIGLA_PAIS, NOME_PAIS, ATIVO });
      setModalOpen(true);
    }

    try {
      setControlEdit({
        loading: true,
        isInUse: true,
      });
      const { inUse } = await isInUse(id);
      setControlEdit({
        loading: false,
        isInUse: inUse,
      });
    } catch (error) {
      enqueueSnackbar("It's not posible to edit, try again later", {
        variant: "error",
      });
      setControlEdit({
        loading: false,
        isInUse: true,
      });
    }
  };

  const handleDeleteCountryConfirmation = (id: string | number) => {
    setCountryId(id);
    setOpenDeleteModal(true);
  };

  const handleDeleteCountry = async () => {
    if (countryId)
      try {
        setIsSaving(true);
        await deleteCountry(countryId);
        enqueueSnackbar("Country deleted successful", { variant: "success" });
        setParams({ ...params });
      } catch (err: any) {
        if (err.response.status) {
          enqueueSnackbar("Country is being used", { variant: "error" });
        } else {
          enqueueSnackbar(err.response.data.message[0], { variant: "error" });
        }
      }
    setIsSaving(false);
    setOpenDeleteModal(false);
  };

  const handleFormSubmit = async (newCountry: Country) => {
    try {
      setIsSaving(true);
      if (newCountry.ID_PAIS) {
        await update(newCountry.ID_PAIS, newCountry);
        enqueueSnackbar("Country updated successful", { variant: "success" });
      } else {
        await create(newCountry);
        enqueueSnackbar("Country created successful", { variant: "success" });
      }
      setCountry({ ATIVO: true } as Country);
      setParams({ ...params });
      setModalOpen(false);
      setIsSaving(false);
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        setErrors([err.response?.data?.message]);
      }
      setIsSaving(false);
    }
    setIsSaving(false);
  };

  const exportCountries = async () => {
    try {
      await exportAll(params);
    } catch (err) {}
  };

  const handleCancelEditCountry = () => {
    setCountry({} as Country);
    setErrors([]);
    setModalOpen(false);
    setControlEdit({
      loading: false,
      isInUse: false,
    });
  };

  const onViewCountry = (id: string | number) => {
    setErrors([]);
    setReadOnly(true)
    const editCountry = data.findLast((item: any) => item.ID_PAIS === id);
    if (editCountry) {
      const { ID_PAIS, SIGLA_PAIS, NOME_PAIS, ATIVO } = editCountry;
      setCountry({ ID_PAIS, SIGLA_PAIS, NOME_PAIS, ATIVO });
      setModalOpen(true);
    }
  };

  return (
    <Grid marginTop={4}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={10}>
          <Typography variant="h5">Country</Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center" marginTop={1}>
        <Grid item xs={2}>
          {!modalOpen ? (
            <Grid item xs={2}>
              <Button
                sx={{
                  display: hasClaim("Country - Maintenance") ? "block" : "none",
                }}
                variant="contained"
                onClick={() => {
                  setControlEdit({
                    loading: false,
                    isInUse: false,
                  });
                  setErrors([])
                  setReadOnly(false);
                  setModalOpen(true);
                }}
                fullWidth
                data-testid="new-button"
              >
                New
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
      {modalOpen ? (
        <Grid container alignItems="center">
          <CountryForm
            onSubmit={handleFormSubmit}
            country={country}
            onCancel={handleCancelEditCountry}
            isLoading={isSaving}
            errors={errors}
            controlEdit={controlEdit}
            readOnly={readOnly}
          />
        </Grid>
      ) : null}
      <Search onChange={handleRequest} />
      <Grid container marginTop={2} justifyContent="flex-end">
        <Button
          variant="text"
          onClick={exportCountries}
          startIcon={<Download />}
        >
          Export XLSX
        </Button>
      </Grid>
      <Grid>
        <DataTable
          loading={isLoading}
          data={data}
          userValidation={userValidation}
          onDeleteCountry={handleDeleteCountryConfirmation}
          onEditCountry={handleEditCountry}
          onViewCountry={onViewCountry}
          rowCount={meta.total || 0}
          onPaginationModelChange={(e: any) => {
            setPaginationModel(e);
            handleRequest({ page: e.page + 1, limit: e.pageSize });
          }}
          pagination
          paginationModel={paginationModel}
          initialState={{
            pagination: { paginationModel: { pageSize: params.limit } },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode="server"
        />
      </Grid>
      <Dialog
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete Country</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure that you want to delete this Country?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
          <Button onClick={handleDeleteCountry} data-testid="delete-modal-confirm">Confirm</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
